import { useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSWRConfig } from 'swr'
import { apiPost } from '../config/api'
import {
  appendCustomerIdToReturn,
  resolveCustomerCreateReturn,
} from '../utils/customerCreateReturn'
import type { CustomerDto } from '../types/customer'
import {
  Alert,
  AppCard,
  AppPage,
  Button,
  Field,
  TextInput,
} from './UI'
import {
  CustomerExtraFields,
  EMPTY_CUSTOMER_EXTRA_FIELDS,
  customerExtraFieldsToPayload,
  type CustomerExtraFieldsValue,
} from './customers/CustomerExtraFields'

export function CreateCustomer() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { returnTo, simplifiedForm } = resolveCustomerCreateReturn(searchParams)
  const { mutate } = useSWRConfig()
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [tagsInput, setTagsInput] = useState('')
  const [extra, setExtra] = useState<CustomerExtraFieldsValue>(
    EMPTY_CUSTOMER_EXTRA_FIELDS,
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim() || saving) return
    const p = phone.trim()
    const em = email.trim()
    if (simplifiedForm && !p) {
      setError('Συμπληρώστε τηλέφωνο.')
      return
    }
    if (!simplifiedForm && !p && !em) {
      setError('Συμπληρώστε τουλάχιστον τηλέφωνο ή email.')
      return
    }
    setError(null)
    setSaving(true)
    try {
      const payload: Record<string, string | string[] | boolean | undefined> = {
        name: name.trim(),
      }
      if (p) payload.phone = p
      if (em) payload.email = em
      const rawTags = tagsInput
        .split(/[,;]/)
        .map((t) => t.trim())
        .filter(Boolean)
      if (rawTags.length > 0) payload.tags = rawTags
      Object.assign(payload, customerExtraFieldsToPayload(extra))
      const created = await apiPost<CustomerDto>('/customers', payload)
      await mutate((key) => Array.isArray(key) && key[0] === 'crm-customers')
      if (returnTo) {
        navigate(appendCustomerIdToReturn(returnTo, created.id))
      } else {
        navigate('/customers')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Η αποθήκευση απέτυχε.')
    } finally {
      setSaving(false)
    }
  }

  function goBack() {
    if (returnTo) navigate(returnTo)
    else navigate('/customers')
  }

  return (
    <AppPage size="narrow">
      <p className="mb-6">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={goBack}
        >
          {returnTo
            ? returnTo.startsWith('/offers')
              ? '← Πίσω στην προσφορά'
              : '← Πίσω'
            : '← Πελάτες'}
        </Button>
      </p>
      <form
        onSubmit={(ev) => void handleSubmit(ev)}
      >
        <AppCard className="overflow-hidden p-0">
          <div className="border-b border-zinc-200/80 bg-gradient-to-r from-violet-50/90 to-indigo-50/60 px-5 py-4 dark:border-zinc-800 dark:from-violet-950/30 dark:to-indigo-950/20 sm:px-6">
            <p className="text-sm font-semibold text-zinc-900 dark:text-white">
              Βασικά στοιχεία
            </p>
            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
              Τα πεδία με{' '}
              <span className="font-medium text-red-600 dark:text-red-400">*</span>{' '}
              είναι υποχρεωτικά
              {!simplifiedForm ? ' (τηλέφωνο ή email, ένα από τα δύο)' : ''}.
            </p>
          </div>

          <div className="grid gap-5 p-5 sm:grid-cols-2 sm:p-6">
            <div className="sm:col-span-2">
              <Field label="Όνομα / επωνυμία" htmlFor="cust_name" required>
                <TextInput
                  id="cust_name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoComplete="name"
                  placeholder="π.χ. Γιώργος Παπαδόπουλος"
                  controlSize="md"
                />
              </Field>
            </div>

            {!simplifiedForm ? (
            <div className="sm:col-span-2">
            <Field
              label={
                <>
                  Ετικέτες{' '}
                  <span className="font-normal text-zinc-500">(κόμμα ή ;)</span>
                </>
              }
              htmlFor="cust_tags"
            >
              <TextInput
                id="cust_tags"
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="π.χ. αθήνα, επείγον"
                controlSize="md"
              />
            </Field>
            </div>
            ) : null}

            <Field
              label="Τηλέφωνο"
              htmlFor="cust_phone"
              required
              hint={
                !simplifiedForm ? (
                  <span className="text-red-600/90 dark:text-red-400/90">
                    Υποχρεωτικό αν δεν συμπληρωθεί email.
                  </span>
                ) : undefined
              }
            >
              <TextInput
                id="cust_phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                placeholder="π.χ. 697 123 4567"
                controlSize="md"
              />
            </Field>

            {!simplifiedForm ? (
            <Field
              label="Email"
              htmlFor="cust_email"
              required
              hint={
                <span className="text-red-600/90 dark:text-red-400/90">
                  Υποχρεωτικό αν δεν συμπληρωθεί τηλέφωνο.
                </span>
              }
            >
              <TextInput
                id="cust_email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="π.χ. name@example.gr"
                controlSize="md"
              />
            </Field>
            ) : null}

            {error ? (
              <div className="sm:col-span-2">
                <Alert tone="error" className="text-left">
                  {error}
                </Alert>
              </div>
            ) : null}
          </div>

          {!simplifiedForm ? (
            <>
              <div className="border-t border-zinc-200/80 px-5 py-4 dark:border-zinc-800 sm:px-6">
                <p className="text-sm font-semibold text-zinc-900 dark:text-white">
                  Στοιχεία πελάτη
                </p>
                <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                  Προαιρετικά — για τιμολόγηση και παρακολούθηση προέλευσης.
                </p>
              </div>

              <div className="grid gap-5 p-5 sm:grid-cols-2 sm:p-6 sm:pt-4">
                <CustomerExtraFields
                  value={extra}
                  onChange={(patch) => setExtra((prev) => ({ ...prev, ...patch }))}
                />
              </div>
            </>
          ) : null}

          <div className="flex flex-col gap-3 border-t border-zinc-200/80 bg-zinc-50/70 px-5 py-4 dark:border-zinc-800 dark:bg-zinc-950/30 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              {returnTo
                ? 'Μετά την αποθήκευση ή το κλείσιμο επιστρέφεις στην προηγούμενη οθόνη.'
                : 'Θα εμφανιστεί στη λίστα πελατών.'}
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              {returnTo ? (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={saving}
                  onClick={goBack}
                  className="sm:min-w-32"
                >
                  Κλείσιμο
                </Button>
              ) : null}
              <Button
                type="submit"
                disabled={
                  !name.trim() ||
                  saving ||
                  (simplifiedForm
                    ? !phone.trim()
                    : !phone.trim() && !email.trim())
                }
                className="sm:min-w-44"
              >
                {saving ? 'Αποθήκευση…' : 'Αποθήκευση πελάτη'}
              </Button>
            </div>
          </div>
        </AppCard>
      </form>
    </AppPage>
  )
}
